import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import EstimateModal from './EstimateModal';
import './RoomManager.css';

const RoomManager = () => {
  const { shapes, mergedStructures } = useApp();
  const [expandedRooms, setExpandedRooms] = useState({});
  const [showEstimateModal, setShowEstimateModal] = useState(false);

  // Helper function to calculate area for any shape type
  const calculateShapeArea = (shape) => {
    switch (shape.type) {
      case 'rectangle':
        // For rectangles, check if they have curved sides
        if (shape.lineData && shape.lineData.some(line => line && line.isCurveMode)) {
          // For curved rectangles, use approximation based on curve data
          const baseArea = (shape.width * shape.height) / 10000; // Convert to square meters
          // Add small adjustment for curves (typically curves add 5-15% more area)
          const curveCount = shape.lineData.filter(line => line && line.isCurveMode).length;
          const curveAdjustment = curveCount * 0.1; // 10% per curved side
          return baseArea * (1 + curveAdjustment);
        }
        return (shape.width * shape.height) / 10000; // Convert to square meters
        
      case 'circle':
        const area = Math.PI * Math.pow(shape.radius / 100, 2); // Convert radius to meters
        return area;
        
      case 'triangle':
        // For triangle, use base * height / 2 formula
        const triangleArea = (shape.width * shape.height / 2) / 10000; // Convert to square meters
        return triangleArea;
        
      case 'door':
        // For door (semi-circle), use (π × r²) / 2 formula
        const radius = shape.radius || 50;
        const doorArea = (Math.PI * Math.pow(radius / 100, 2)) / 2; // Convert to square meters
        return doorArea;
        
      case 'line':
        // Lines don't have area, but if they're part of a curved structure
        return 0;
        
      case 'polygonRoom':
        if (shape.vertices && shape.vertices.length > 2) {
          let area = 0;
          for (let i = 0, j = shape.vertices.length - 1; i < shape.vertices.length; j = i++) {
            area += (shape.vertices[j].x + shape.vertices[i].x) * (shape.vertices[j].y - shape.vertices[i].y);
          }
          return Math.abs(area / 2) / 10000;
        }
        return 0;
        
      default:
        console.warn(`Unknown shape type for area calculation: ${shape.type}`);
        return 0;
    }
  };

  // Helper function to calculate perimeter for any shape type
  const calculateShapePerimeter = (shape) => {
    switch (shape.type) {
      case 'rectangle':
        let perimeter = 2 * (shape.width + shape.height) / 100; // Convert to meters
        
        // Adjust for curved sides
        if (shape.lineData && shape.lineData.some(line => line && line.isCurveMode)) {
          const curveCount = shape.lineData.filter(line => line && line.isCurveMode).length;
          // Curves typically add 15-25% more length
          perimeter *= (1 + curveCount * 0.2);
        }
        return perimeter;
        
      case 'circle':
        return 2 * Math.PI * (shape.radius / 100); // Convert to meters
        
      case 'triangle':
        // Estimate perimeter as sum of three sides (approximation)
        const side1 = shape.width / 100;
        const side2 = shape.height / 100;
        const side3 = Math.sqrt(Math.pow(side1, 2) + Math.pow(side2, 2)); // Hypotenuse
        return side1 + side2 + side3;
        
      case 'door':
        // For door (semi-circle), perimeter is π × r (half circle) + diameter
        const doorRadius = shape.radius || 50;
        const halfCircle = Math.PI * (doorRadius / 100);
        const diameter = 2 * (doorRadius / 100);
        return halfCircle + diameter;
        
      case 'polygonRoom':
        if (shape.vertices && shape.vertices.length > 1) {
          let peri = 0;
          for (let i = 0, j = shape.vertices.length - 1; i < shape.vertices.length; j = i++) {
            const dx = shape.vertices[i].x - shape.vertices[j].x;
            const dy = shape.vertices[i].y - shape.vertices[j].y;
            peri += Math.sqrt(dx * dx + dy * dy);
          }
          return peri / 100;
        }
        return 0;
        
      default:
        return 0;
    }
  };

  // Helper function to get equivalent rectangular dimensions for carpet planning
  const getEquivalentDimensions = (shape) => {
    const area = calculateShapeArea(shape);
    
    switch (shape.type) {
      case 'rectangle':
        return {
          width: shape.width / 100,
          height: shape.height / 100,
          type: 'rectangular',
          hasCurves: shape.lineData && shape.lineData.some(line => line && line.isCurveMode)
        };
        
      case 'circle':
        // Convert circle to equivalent square for carpet planning
        const diameter = (shape.radius * 2) / 100;
        return {
          width: diameter,
          height: diameter,
          type: 'circular',
          originalRadius: shape.radius / 100
        };
        
      case 'triangle':
        return {
          width: shape.width / 100,
          height: shape.height / 100,
          type: 'triangular'
        };
        
      case 'door':
        // For door (semi-circle), use diameter as both width and height for carpet planning
        const doorDiameter = (shape.radius * 2) / 100;
        return {
          width: doorDiameter,
          height: doorDiameter / 2, // Semi-circle height
          type: 'door',
          originalRadius: shape.radius / 100
        };
        
      default:
        // Fallback to square based on area
        const side = Math.sqrt(area);
        return {
          width: side,
          height: side,
          type: 'irregular'
        };
    }
  };

  // Helper function to calculate bounding box for merged structures with accurate area
  const calculateAccurateBounds = (shapes) => {
    if (!shapes || shapes.length === 0) return { width: 0, height: 0, area: 0, perimeter: 0 };

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let totalArea = 0;
    let totalPerimeter = 0;

    shapes.forEach(shape => {
      // Calculate accurate area for each shape
      totalArea += calculateShapeArea(shape);
      totalPerimeter += calculateShapePerimeter(shape);

      // Calculate bounding box
      let left, top, right, bottom;
      
      switch (shape.type) {
        case 'rectangle':
          left = shape.x;
          top = shape.y;
          right = shape.x + shape.width;
          bottom = shape.y + shape.height;
          break;
          
        case 'circle':
          left = shape.x;
          top = shape.y;
          right = shape.x + (shape.radius * 2);
          bottom = shape.y + (shape.radius * 2);
          break;
          
        case 'triangle':
          left = shape.x;
          top = shape.y;
          right = shape.x + shape.width;
          bottom = shape.y + shape.height;
          break;
          
        case 'door':
          left = shape.x;
          top = shape.y;
          right = shape.x + (shape.radius * 2);
          bottom = shape.y + shape.radius; // Semi-circle height
          break;
          
        default:
          left = shape.x;
          top = shape.y;
          right = shape.x + (shape.width || shape.radius * 2 || 100);
          bottom = shape.y + (shape.height || shape.radius * 2 || 100);
      }

      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);
    });

    const boundingWidth = (maxX - minX) / 100; // Convert to meters
    const boundingHeight = (maxY - minY) / 100; // Convert to meters

    return {
      width: boundingWidth,
      height: boundingHeight,
      area: totalArea,
      perimeter: totalPerimeter,
      x: minX / 100,
      y: minY / 100
    };
  };

  // Group shapes by their rough position to identify rooms
  const groupShapesByProximity = (shapes) => {
    if (!shapes || shapes.length === 0) return [];
    
    const groups = [];
    const processed = new Set();
    const proximityThreshold = 50; // pixels

    shapes.forEach((shape, index) => {
      if (processed.has(index)) return;

      const group = [shape];
      processed.add(index);

      // Find nearby shapes
      shapes.forEach((otherShape, otherIndex) => {
        if (processed.has(otherIndex) || index === otherIndex) return;

        const distance = Math.sqrt(
          Math.pow(shape.x - otherShape.x, 2) + 
          Math.pow(shape.y - otherShape.y, 2)
        );

        if (distance < proximityThreshold) {
          group.push(otherShape);
          processed.add(otherIndex);
        }
      });

      groups.push(group);
    });

    return groups;
  };

  // Create individual rooms from single shapes
  const createIndividualRooms = (shapes) => {
    return shapes
      .filter(shape => shape.type === 'rectangle' || shape.type === 'polygonRoom')
      .map((shape, index) => {
        const area = calculateShapeArea(shape);
        const perimeter = calculateShapePerimeter(shape);
        let width = 0, height = 0, pos = { x: 0, y: 0 };
        if (shape.type === 'rectangle') {
          width = shape.width / 100;
          height = shape.height / 100;
          pos = { x: shape.x, y: shape.y };
        } else if (shape.type === 'polygonRoom' && shape.vertices && shape.vertices.length > 2) {
          const xs = shape.vertices.map(v => v.x);
          const ys = shape.vertices.map(v => v.y);
          width = (Math.max(...xs) - Math.min(...xs)) / 100;
          height = (Math.max(...ys) - Math.min(...ys)) / 100;
          pos = { x: Math.min(...xs), y: Math.min(...ys) };
        }
        return {
          id: `room-${shape.id || index}`,
          name: `Room ${index + 1}`,
          area: area.toFixed(2),
          perimeter: perimeter.toFixed(2),
          width: width.toFixed(1),
          height: height.toFixed(1),
          position: pos,
          type: 'individual',
          shapeType: shape.type,
          carpetComplexity: 'simple', // Could be enhanced for polygons
          shapes: [shape]
        };
      });
  };

  // Create merged rooms from merged structures
  const createMergedRooms = (structures) => {
    return structures.map((structure, index) => {
      const bounds = calculateAccurateBounds(structure.shapes);
      const hasNonRectangular = structure.shapes.some(s => s.type !== 'rectangle');
      const hasCurvedRectangles = structure.shapes.some(s => 
        s.type === 'rectangle' && s.lineData && s.lineData.some(line => line && line.isCurveMode)
      );
      
      return {
        id: `merged-${index}`,
        name: `Complex Room ${index + 1}`,
        area: bounds.area.toFixed(2),
        perimeter: bounds.perimeter.toFixed(2),
        width: bounds.width.toFixed(1),
        height: bounds.height.toFixed(1),
        position: { x: bounds.x, y: bounds.y },
        type: 'merged',
        complexity: structure.shapes.length,
        hasNonRectangular,
        hasCurvedRectangles,
        shapes: structure.shapes
      };
    });
  };

  // Get all available shapes (not in merged structures)
  const getAvailableShapes = () => {
    const mergedShapeIds = new Set();
    if (mergedStructures) {
      mergedStructures.forEach(structure => {
        structure.shapes.forEach(shape => {
          mergedShapeIds.add(shape.id);
        });
      });
    }
    // Only include rectangles (rooms)
    return shapes.filter(shape => !mergedShapeIds.has(shape.id) && shape.type === 'rectangle');
  };

  // Create room data
  const availableShapes = getAvailableShapes();
  const individualRooms = createIndividualRooms(availableShapes);
  const mergedRooms = mergedStructures ? createMergedRooms(mergedStructures) : [];
  const rooms = [...individualRooms, ...mergedRooms];

  const toggleRoom = (roomId) => {
    setExpandedRooms(prev => ({
      ...prev,
      [roomId]: !prev[roomId]
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEstimate = () => {
    setShowEstimateModal(true);
  };

  const totalArea = rooms.reduce((sum, room) => sum + parseFloat(room.area), 0);

  return (
    <div className="room-manager">
      <div className="room-header">
      <h2 className="room-title ">Room Analysis</h2>
        <div className="header-content">
   
      
          
          <div className="action-buttons">
            <button className="btn btn-print" onClick={handlePrint}>
              <div className="btn-content">
                <span className="btn-icon">🖨️</span>
                <span>Print</span>
              </div>
            </button>
            <button className="btn btn-estimate" onClick={handleEstimate}>
              <div className="btn-content">
                <span className="btn-icon">📊</span>
                <span>Estimate</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="room-content">
        <div className="summary-stats">
          <div className="summary-title">Project Overview</div>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">{rooms.length}</div>
              <div className="stat-label">Total Rooms</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{totalArea.toFixed(1)}</div>
              <div className="stat-label">Area (m²)</div>
            </div>
          </div>
        </div>

        <div className="rooms-container">
          {rooms.map((room) => {
            const isExpanded = expandedRooms[room.id];
            const roomTypeInfo = getRoomTypeInfo(room);
            
            return (
              <div key={room.id} className={`room-card ${isExpanded ? 'expanded' : ''}`}>
                <div className="room-card-header" onClick={() => toggleRoom(room.id)}>
                  <div className="room-info">
                    <div className="room-icon">
                      {roomTypeInfo.icon}
                    </div>
                    <div className="room-details">
                      <div className="room-name">{room.name}</div>
                      <div className="room-stats">
                        <div className="room-stat">
                          <div className="room-stat-value">{room.width}m</div>
                          <div className="room-stat-label">Width</div>
                        </div>
                        <div className="room-stat">
                          <div className="room-stat-value">{room.height}m</div>
                          <div className="room-stat-label">Height</div>
                        </div>
                        <div className="room-stat">
                          <div className="room-stat-value">{room.area}</div>
                          <div className="room-stat-label">Area</div>
                        </div>
                      </div>
                      <div className={`room-type-badge ${roomTypeInfo.badgeClass}`}>
                        {roomTypeInfo.badge}
                      </div>
                    </div>
                    <div className="expand-indicator">
                      ▼
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="room-expanded-content">
                    <div className="expanded-stats">
                      <div className="expanded-stat">
                        <div className="expanded-stat-value">{room.perimeter}m</div>
                        <div className="expanded-stat-label">Perimeter</div>
                      </div>
                      <div className="expanded-stat">
                        <div className="expanded-stat-value">{room.position.x.toFixed(0)}</div>
                        <div className="expanded-stat-label">X Position</div>
                      </div>
                      <div className="expanded-stat">
                        <div className="expanded-stat-value">{room.position.y.toFixed(0)}</div>
                        <div className="expanded-stat-label">Y Position</div>
                      </div>
                      {room.type === 'merged' && (
                        <div className="expanded-stat">
                          <div className="expanded-stat-value">{room.complexity}</div>
                          <div className="expanded-stat-label">Shapes</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showEstimateModal && (
        <EstimateModal 
          rooms={rooms} 
          onClose={() => setShowEstimateModal(false)} 
        />
      )}
    </div>
  );

  // Helper function to get room type information
  function getRoomTypeInfo(room) {
    if (room.type === 'merged') {
      if (room.hasNonRectangular || room.hasCurvedRectangles) {
        return {
          icon: '🏢',
          badge: `Complex (${room.complexity})`,
          badgeClass: 'badge-complex'
        };
      }
      return {
        icon: '🏗️',
        badge: `Merged (${room.complexity})`,
        badgeClass: 'badge-merged'
      };
    } else {
      const shapeIcons = {
        'circle': '🔵',
        'triangle': '🔺',
        'rectangle': '🔲'
      };
      const icon = shapeIcons[room.shapeType] || '📐';
      
      if (room.carpetComplexity === 'complex') {
        return {
          icon: icon,
          badge: room.shapeType === 'rectangle' ? 'Curved' : room.shapeType.charAt(0).toUpperCase() + room.shapeType.slice(1),
          badgeClass: 'badge-complex'
        };
      }
      
      return {
        icon: icon,
        badge: 'Standard',
        badgeClass: 'badge-individual'
      };
    }
  }
};

export default RoomManager; 